import React, {
  useEffect,
  useLayoutEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowDown,
  ArrowUp,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../api";
import {
  Alert,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
} from "../ui";
import { serializeStatementXmlSegments } from "./parse";
import {
  createEmptySentenceRow,
  parseRowEditedFlag,
  segmentsToSentenceRows,
  sentenceRowsToSegments,
} from "./rows";
import type { StatementSegment, StatementSentenceRow } from "./types";
import "./statement-editor.css";

export type ReferenceIssue = {
  row_index: number | null;
  node_id?: string | null;
  code?: string | null;
  reason: string;
};
export type StatementXmlEditorProps = {
  segments: StatementSegment[];
  onSegmentsChange: (segments: StatementSegment[]) => void;
  language: string;
  sx?: unknown;
  disabled?: boolean;
  showBottomAdd?: boolean;
  disableClpAssignment?: boolean;
  referenceSegments?: StatementSegment[] | null;
  referenceLabel?: string;
  referenceIssues?: ReferenceIssue[];
};
type Phrase = {
  group_id: number;
  variant: number;
  regulatory_code: string;
  text: string;
  text_base: string;
  has_variable: boolean;
};
const numeric = (value: string | undefined) =>
  value !== undefined && /^\d+$/.test(value) ? Number(value) : null;
const codeEqual = (a: string, b: string) =>
  a.replace(/\s/g, "").toUpperCase() === b.replace(/\s/g, "").toUpperCase();
const incomplete = (row: StatementSentenceRow) =>
  row.nodeType === "GlossaryStatementNode" &&
  (!row.code ||
    numeric(row.groupId) === null ||
    numeric(row.variantId) === null ||
    typeof row.hasVariable !== "boolean");
function assign(
  row: StatementSentenceRow,
  phrase: Phrase | null,
): StatementSentenceRow {
  const attributes: Record<string, string> = {
    ...row.attributes,
    isEdited: "true",
  };
  delete attributes.matchStatus;
  if (!phrase) {
    for (const key of ["code", "groupId", "variantId", "hasVariable"])
      delete attributes[key];
    return {
      ...row,
      sourceKind: "node",
      nodeType: "StatementNode",
      code: undefined,
      groupId: undefined,
      variantId: undefined,
      hasVariable: undefined,
      isEdited: true,
      attributes,
    };
  }
  const next = {
    code: phrase.regulatory_code,
    groupId: String(phrase.group_id),
    variantId: String(phrase.variant),
    hasVariable: phrase.has_variable,
  };
  return {
    ...row,
    ...next,
    sourceKind: "node",
    nodeType: "GlossaryStatementNode",
    isEdited: true,
    text: row.text.trim() ? row.text : phrase.text,
    attributes: {
      ...attributes,
      code: next.code,
      groupId: next.groupId,
      variantId: next.variantId,
      hasVariable: String(next.hasVariable),
    },
  };
}
export function StatementXmlEditor({
  segments,
  onSegmentsChange,
  language,
  disabled = false,
  showBottomAdd = true,
  disableClpAssignment = false,
  referenceSegments,
  referenceLabel,
  referenceIssues = [],
}: StatementXmlEditorProps) {
  const { t } = useTranslation("ui");
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const editorId = useId().replace(/:/g, "");
  const [rows, setRows] = useState(() => segmentsToSentenceRows(segments));
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const lastEmitted = useRef<string | null>(null);
  const currentLanguage = useRef(language);
  currentLanguage.current = language;
  const lastLanguage = useRef(language);
  const [active, setActive] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<Phrase[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [optionIndex, setOptionIndex] = useState(-1);
  const searchSequence = useRef(0);
  const [restorePending, setRestorePending] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [announcement, setAnnouncement] = useState("");
  const [removed, setRemoved] = useState<{
    row: StatementSentenceRow;
    index: number;
  } | null>(null);
  useEffect(() => {
    const value = serializeStatementXmlSegments(segments);
    if (lastLanguage.current === language && lastEmitted.current === value)
      return;
    lastLanguage.current = language;
    lastEmitted.current = null;
    setRows(segmentsToSentenceRows(segments));
    setActive(null);
    setRowErrors({});
    setRemoved(null);
    searchSequence.current++;
  }, [segments, language]);
  const emit = (next: StatementSentenceRow[]) => {
    rowsRef.current = next;
    setRows(next);
    const nextSegments = sentenceRowsToSegments(next);
    lastEmitted.current = serializeStatementXmlSegments(nextSegments);
    onSegmentsChange(nextSegments);
  };
  const referenceRows = useMemo(
    () => (referenceSegments ? segmentsToSentenceRows(referenceSegments) : []),
    [referenceSegments],
  );
  useEffect(() => {
    if (!active || disabled || disableClpAssignment) return;
    const row = rowsRef.current.find((r) => r.rowId === active);
    if (!row) return;
    const sequence = ++searchSequence.current;
    setSearching(true);
    setSearchError(false);
    setOptionIndex(-1);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ language, limit: "20" });
      if (query.trim()) params.set("q", query.trim());
      if (row.text.trim())
        params.set("sentence", row.text.trim().slice(0, 300));
      if (numeric(row.groupId) !== null) {
        params.set("selected_group_id", row.groupId!);
        params.set("selected_variant", row.variantId || "0");
      }
      apiRef.current
        .get(`/translations/clp-codes/search?${params}`)
        .then((data) => {
          if (sequence === searchSequence.current) {
            setOptions(Array.isArray(data) ? data : []);
            setSearching(false);
          }
        })
        .catch(() => {
          if (sequence === searchSequence.current) {
            setOptions([]);
            setSearching(false);
            setSearchError(true);
          }
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      searchSequence.current++;
    };
  }, [active, query, language, disabled, disableClpAssignment]);
  const choose = (id: string, phrase: Phrase | null) => {
    if (disabled || disableClpAssignment) return;
    emit(rowsRef.current.map((r) => (r.rowId === id ? assign(r, phrase) : r)));
    setActive(null);
    setRowErrors((prev) => ({ ...prev, [id]: "" }));
  };
  const move = (index: number, delta: number) => {
    const next = [...rowsRef.current];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const [row] = next.splice(index, 1);
    next.splice(target, 0, row);
    emit(next);
    setAnnouncement(
      t("editor.moved", { number: index + 1, position: target + 1 }),
    );
  };
  const restore = async (id: string) => {
    const row = rowsRef.current.find((r) => r.rowId === id);
    if (!row || disabled || !disableClpAssignment || restorePending) return;
    const languageAtStart = language;
    const initial = JSON.stringify(row);
    const group = numeric(row.groupId);
    const variant = numeric(row.variantId);
    if (incomplete(row)) {
      setRowErrors((prev) => ({ ...prev, [id]: t("editor.incomplete") }));
      return;
    }
    const params = new URLSearchParams({
      language,
      variant: String(variant ?? 0),
    });
    if (group !== null) params.set("group_id", String(group));
    if (row.code) params.set("code", row.code);
    setRestorePending(id);
    setRowErrors((prev) => ({ ...prev, [id]: "" }));
    try {
      const phrase = (await apiRef.current.get(
        `/translations/clp-codes/exact?${params}`,
      )) as Phrase;
      const current = rowsRef.current.find((r) => r.rowId === id);
      if (
        !current ||
        languageAtStart !== currentLanguage.current ||
        JSON.stringify(current) !== initial
      )
        return;
      if (
        (group !== null &&
          (phrase.group_id !== group || phrase.variant !== (variant ?? 0))) ||
        (row.code && !codeEqual(row.code, phrase.regulatory_code)) ||
        !phrase.text?.trim()
      )
        throw { status: 409 };
      emit(
        rowsRef.current.map((r) =>
          r.rowId === id
            ? {
                ...r,
                text: phrase.text,
                isEdited: true,
                attributes: { ...r.attributes, isEdited: "true" },
              }
            : r,
        ),
      );
      setAnnouncement(t("editor.restored"));
    } catch (error: any) {
      setRowErrors((prev) => ({
        ...prev,
        [id]: t(
          error?.status === 404
            ? "editor.restoreMissing"
            : error?.status === 409
              ? "editor.restoreConflict"
              : "editor.restoreFailed",
        ),
      }));
    } finally {
      setRestorePending(null);
    }
  };
  const onSearchKey = (
    event: React.KeyboardEvent<HTMLInputElement>,
    id: string,
  ) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setActive(null);
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOptionIndex((previous) =>
        options.length
          ? Math.max(
              0,
              Math.min(
                options.length - 1,
                previous + (event.key === "ArrowDown" ? 1 : -1),
              ),
            )
          : -1,
      );
    }
    if (event.key === "Enter" && optionIndex >= 0 && options[optionIndex]) {
      event.preventDefault();
      choose(id, options[optionIndex]);
    }
  };
  return (
    <div className="statement-editor" lang={language}>
      <div className="sr-only" role="status" aria-live="polite">
        {announcement}
      </div>
      {rows.length === 0 && <p className="vl-muted">{t("editor.empty")}</p>}
      {rows.map((row, index) => {
        const issue = referenceIssues.find(
          (item) =>
            (item.node_id && item.node_id === row.id) ||
            item.row_index === index,
        );
        const problem =
          row.attributes.matchStatus || incomplete(row) || issue?.reason;
        const reference =
          referenceRows.find(
            (candidate) => row.id && candidate.id === row.id,
          ) ||
          referenceRows.find(
            (candidate) =>
              row.groupId &&
              candidate.groupId === row.groupId &&
              candidate.variantId === row.variantId &&
              candidate.code === row.code,
          );
        const listId = `${editorId}-phrases-${index}`;
        return (
          <section
            role="group"
            className={`statement-edit-row${problem ? " statement-has-issue" : ""}`}
            key={row.rowId}
            aria-label={t("editor.row", { number: index + 1 })}
            data-row-id={row.rowId}
          >
            <div className="statement-edit-code">
              <span className="statement-row-number">{index + 1}</span>
              <div>
                {row.code && (
                  <strong className="statement-code">{row.code}</strong>
                )}
                <span className="vl-hint statement-kind">
                  {t(
                    row.nodeType === "GlossaryStatementNode"
                      ? "editor.official"
                      : "editor.custom",
                  )}
                </span>
                {parseRowEditedFlag(row) && row.text.trim() && (
                  <span className="vl-hint statement-kind">
                    {t("editor.edited")}
                  </span>
                )}
              </div>
            </div>
            <div className="statement-edit-content">
              <label className="sr-only" htmlFor={`${editorId}-text-${index}`}>
                {t("editor.wording", { number: index + 1 })}
              </label>
              <WordingTextarea
                id={`${editorId}-text-${index}`}
                value={row.text}
                disabled={disabled}
                onChange={(event) =>
                  emit(
                    rowsRef.current.map((candidate) =>
                      candidate.rowId === row.rowId
                        ? {
                            ...candidate,
                            text: event.target.value,
                            isEdited: true,
                            attributes: {
                              ...candidate.attributes,
                              isEdited: "true",
                            },
                          }
                        : candidate,
                    ),
                  )
                }
              />
              {["signalWord", "contains"].includes(
                row.attributes.contentRole || "",
              ) && <p className="vl-hint">{t("editor.sourceRoleCheck")}</p>}
              {problem && (
                <Alert variant="warning">
                  {t(
                    issue?.reason === "code_conflict"
                      ? "editor.referenceConflict"
                      : incomplete(row)
                        ? "editor.incomplete"
                        : "editor.unresolved",
                  )}
                  {disableClpAssignment && <p>{t("editor.correctSource")}</p>}
                </Alert>
              )}
              {rowErrors[row.rowId] && (
                <Alert variant="error">{rowErrors[row.rowId]}</Alert>
              )}
              {reference?.text && (
                <div className="statement-source">
                  <span className="vl-label">
                    {referenceLabel || t("editor.source")}
                  </span>
                  <p>{reference.text}</p>
                </div>
              )}
              <div className="statement-row-actions">
                <div className="statement-phrase-tools">
                  {!disableClpAssignment && (
                    <Popover
                      open={active === row.rowId}
                      onOpenChange={(open) => {
                        setActive(open ? row.rowId : null);
                        if (open) {
                          setQuery(row.code || "");
                          setOptions([]);
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" disabled={disabled}>
                          <Search />
                          {t("editor.choose")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="statement-lookup"
                        align="start"
                        sideOffset={4}
                        lang={language}
                        aria-label={t("editor.choose")}
                      >
                        <Input
                          autoFocus
                          role="combobox"
                          aria-label={t("editor.search")}
                          placeholder={t("editor.search")}
                          aria-autocomplete="list"
                          aria-expanded="true"
                          aria-controls={listId}
                          aria-activedescendant={
                            optionIndex >= 0
                              ? `${listId}-${optionIndex}`
                              : undefined
                          }
                          value={query}
                          onChange={(event) => setQuery(event.target.value)}
                          onKeyDown={(event) => onSearchKey(event, row.rowId)}
                        />
                        <div
                          role="listbox"
                          id={listId}
                          aria-label={t("editor.results")}
                          className="statement-options"
                        >
                          {options.map((phrase, option) => (
                            <button
                              type="button"
                              role="option"
                              id={`${listId}-${option}`}
                              aria-selected={option === optionIndex}
                              tabIndex={-1}
                              key={`${phrase.group_id}:${phrase.variant}`}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => choose(row.rowId, phrase)}
                            >
                              <strong>{phrase.regulatory_code}</strong>
                              <span>{phrase.text}</span>
                            </button>
                          ))}
                        </div>
                        <p className="vl-hint" role="status">
                          {searching
                            ? t("editor.searching")
                            : searchError
                              ? t("editor.searchFailed")
                              : options.length === 0
                                ? t("editor.noResults")
                                : t("editor.keyboardHelp")}
                        </p>
                      </PopoverContent>
                    </Popover>
                  )}
                  {!disableClpAssignment && (row.code || problem) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => choose(row.rowId, null)}
                    >
                      {t("editor.useCustom")}
                    </Button>
                  )}
                </div>
                <div className="statement-row-tools">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled || index === 0}
                    title={t("editor.moveUp", { number: index + 1 })}
                    aria-label={t("editor.moveUp", { number: index + 1 })}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled || index === rows.length - 1}
                    title={t("editor.moveDown", { number: index + 1 })}
                    aria-label={t("editor.moveDown", { number: index + 1 })}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown />
                  </Button>
                  {disableClpAssignment &&
                    row.nodeType === "GlossaryStatementNode" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={
                          disabled ||
                          restorePending !== null ||
                          issue?.reason === "code_conflict"
                        }
                        title={t("editor.restore")}
                        aria-label={t("editor.restore")}
                        onClick={() => void restore(row.rowId)}
                      >
                        <RotateCcw />
                      </Button>
                    )}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    title={t("editor.remove", { number: index + 1 })}
                    aria-label={t("editor.remove", { number: index + 1 })}
                    onClick={() => {
                      setRemoved({ row, index });
                      emit(
                        rowsRef.current.filter((r) => r.rowId !== row.rowId),
                      );
                    }}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            </div>
          </section>
        );
      })}
      <div className="vl-row statement-editor-footer">
        {showBottomAdd && (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => emit([...rowsRef.current, createEmptySentenceRow()])}
          >
            <Plus />
            {t("editor.add")}
          </Button>
        )}
        {removed && (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => {
              const next = [...rowsRef.current];
              next.splice(Math.min(removed.index, next.length), 0, removed.row);
              emit(next);
              setRemoved(null);
            }}
          >
            {t("editor.undoRemove")}
          </Button>
        )}
      </div>
    </div>
  );
}

// Full regulated wording stays readable at every width without an inner scroll area.
function WordingTextarea(props: React.ComponentProps<typeof Textarea>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const resize = () => {
    const node = ref.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight + node.offsetHeight - node.clientHeight}px`;
  };
  useLayoutEffect(resize, [props.value]);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    let width = node.clientWidth;
    const observer = new ResizeObserver(() => {
      if (width !== node.clientWidth) {
        width = node.clientWidth;
        resize();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <Textarea
      {...props}
      ref={ref}
      rows={1}
      style={{
        ...props.style,
        minHeight: 40,
        overflow: "hidden",
        resize: "none",
      }}
    />
  );
}
